import { getStoreState } from 'base/store';
import siteConf from 'pro/config/site';
import { getClientCanonicalUrl } from 'base/utils/getCanonicalUrl';
import { transJsonKeys } from 'ava-util/dist/data';
import { ITrackVariant } from 'base/interfaces/product';
import {
    ISpElementData,
    getSpClientId,
    getSpSessionId,
    getSpAppId,
    spStartTrack,
    spPageView,
    spCartItem,
    spProductsImpression,
    spProductClick,
    spCheckout,
    spStructEvent,
    spElement,
    TSpElementAction,
} from 'base/utils/tracker-sp';
import {
    getGaClientId,
    gaStartTrack,
    gaPageView,
    gaEvent,
    IGaEvent
} from 'base/utils/tracker-ga';
import {
    getFbClientId,
    // fbStartTrack,
    // fbPageView,
    // fbEvent,
    // fbCustomEvent,
    // IFbEventData
} from 'base/utils/tracker-fb';
import {
    appPageView,
    appProductsImpression,
    appProductClick,
    appCtrImpressions,
    appCtrClick,
    appAddToCart
} from 'base/utils/tracker-app';
import { gtmEvent, gtmStartTrack } from 'base/utils/tracker-gtm';
import { generateTrackEventId } from 'base/utils/randomGenerator';
import { storage } from 'base/utils/localStorage';
import { STORAGE_DISABLE_TRACKER, COOKIE_COUNTRY } from 'base/config/constant';
import { getTrackerSkuPrefix } from 'base/utils/product';
import { calcRetailPrice } from 'base/utils/order';
import { isApp } from 'base/utils/app';
import { ITrackerData } from '@base/lib/Tracker';
import { filterFields } from 'ava-util/dist/data';
import { getCookie } from 'ava-util/dist/cookie';
import { IStartTrackData, IStartTrackDataFull } from 'mod/track/interface';

export type ITrackProducts = Array<Record<string, any>>;

type IPageViewConfig = {
    viewType?: string;
    detail?: string | number;
};

export type ITrackerClientIds = {
    spClientId: string;
    gaClientId: string;
    fbClientId: string;
    spAppId: string;
    spSessionId: string;
}

export type ITrackOrderInfo = {
    id?: string;
    step?: number;
    affiliation?: string;
    revenue?: number | string; //商品总金额，Subtotal
    discount?: number | string;
    total?: number | string; //订单总金额
    shipping?: number | string;
    currency?: string;
    goodsAmount?: number | string;
    tax?: number | string;
    couponId?: string;
    coupon?: string;
    paymentMethodName?: string;
}

export type ITrackCheckoutData = {
    items: ITrackVariant[];
    orderInfo?: Omit<ITrackOrderInfo, 'affiliation' | 'currency'>
};

export type ITrackLead = {
    name: string;
    info?: string;
    currency?: string;
    revenue?: number | string;
};

export type TTrackElementAction = TSpElementAction;
export type ITrackElementData = ISpElementData;

let globalCartId: string; // 初始化同时赋值，用于购物车相关打点
let impressed: Record<string, 1> = {}; //impression点，每页仅记录一次

function getCurrency(): string {
    return 'USD';
}

function getCountry(): string {
    return getCookie(COOKIE_COUNTRY) || 'US';
}

function getSpVariants(variants: ITrackProducts): Record<string, any>[] {
    const currency = getCurrency();

    return variants.map(variant => {
        return 'track' in variant ? {
            ...variant.track,
            currency,
            brand: siteConf.brand.toLowerCase(),
            quantity: variant.num || 1,
            price: variant.shopPrice,
            position: variant.position
        } : {
            ...transJsonKeys(variant, {
                trackId: 'id',
                productTrackCode: 'name',
                variantTrackCode: 'variant',
                categoryTrackCode: 'category',
                num: 'quantity',
                listTrackCode: 'list',
            }, ['position', 'list']),
            currency,
            brand: siteConf.brand,
            quantity: variant.num || 1,
            price: Number(variant.dealPrice) > 0 ? variant.dealPrice : variant.shopPrice
        }
    });
}

function getGaVariants(variants: ITrackProducts): Record<string, any>[] {
    const currency = getCurrency();
    const country = getCountry();
    const skuPrefix = getTrackerSkuPrefix(country);
    return variants.map((variant, index) => {
        const common = {
            currency,
            list_position: index + 1,
            brand: siteConf.brand,
            quantity: variant.num || 1,
        }
        return 'track' in variant ? {
            ...variant.track,
            ...common,
            price: variant.shopPrice,
            id: skuPrefix + variant.track.id
        } : {
            ...transJsonKeys(variant, {
                productTrackCode: 'name',
                variantTrackCode: 'variant',
                categoryTrackCode: 'category',
                num: 'quantity',
                listTrackCode: 'list_name',
            }, []),
            ...common,
            price: Number(variant.dealPrice) > 0 ? variant.dealPrice : variant.shopPrice,
            id: skuPrefix + variant.trackId
        }
    });
}

/*
function getFbContentIds(variants: ITrackVariant[]): string[] {
    return variants.map(variant => variant.trackId || '');
}
*/

/*
function getFbEventData(variants: ITrackVariant[], isVariant: boolean = true, extendData?: Partial<IFbEventData>): IFbEventData {
    const data: IFbEventData = {
        content_ids: getFbContentIds(variants),
        content_type: isVariant ? 'product' : 'product_group',
        contents: getGaVariants(variants),
        currency: getCurrency(),
        value: extendData?.value || calcRetailPrice(variants),
    }
    if (variants.length > 0) {
        data.content_name = variants[0].listTrackCode;
    }
    return { ...data, ...extendData };
}
*/

function getOrderInfo(data: ITrackCheckoutData, step?: number): ITrackOrderInfo {
    return {
        ...data.orderInfo,
        revenue: data.orderInfo?.revenue ? Number(data.orderInfo.revenue).toFixed(2) : calcRetailPrice(data.items),
        currency: getCurrency(),
        affiliation: siteConf.affiliation,
        step,
    }
}

//get ClientId
export async function getTrackerClientIds(): Promise<ITrackerClientIds> {
    const spClientId = await getSpClientId().catch(() => '');
    const gaClientId = await getGaClientId().catch(() => '');
    const spSessionId = await getSpSessionId().catch(() => '');
    return {
        spClientId,
        gaClientId,
        fbClientId: getFbClientId(),
        spSessionId,
        spAppId: getSpAppId(),
    }
}

//set user info
export function startTrack(data: IStartTrackData & { cartId: string; }) {
    if (storage.get(STORAGE_DISABLE_TRACKER) === '1') {
        return;
    }
    if (!isApp()) {
        const rootState = getStoreState('rootStore') || {};
        globalCartId = data.cartId;
        const startTrackData: IStartTrackDataFull = {
            userId: data.userId,
            email: data.email,
            sha1Email: data.sha1Email,
            userTags: data.userTags.join(','),
            lang: rootState.lang || 'en',
            country: rootState.country || 'US',
            currency: rootState.currency || 'USD',
        };
        spStartTrack(startTrackData);
        gaStartTrack(startTrackData);
        gtmStartTrack(startTrackData);
        //fbStartTrack(userId, email);
    }
}

//clear track in prev page
export function clearTrack() {
    impressed = {}; //页面跳转后重新打impression点
}

//pageview
export function trackPageView(config?: IPageViewConfig) {
    const rootState = getStoreState('rootStore');
    const viewType = config?.viewType || rootState.viewType || '';
    const eid = generateTrackEventId();
    const canonicalUrl = getClientCanonicalUrl();

    const pageViewData = {
        viewType,
        canonicalUrl,
        detail: config?.detail || rootState.detail || '',
    };

    spPageView(pageViewData);

    appPageView(pageViewData)

    gaPageView(canonicalUrl, eid);

    gtmEvent('PageView', {
        page_code: viewType,
        page_path: canonicalUrl,
        event_id: eid,
    });
}

// 商品列表展示
export function trackProductsImpression(data: ITrackProducts) {
    const eid = generateTrackEventId();
    const currency = getCurrency();
    const MAX_LIST_TRACK = 3;

    const variants = getSpVariants(data);

    spProductsImpression('view', variants);

    appProductsImpression(variants);

    gaEvent('view_item_list', {
        currency,
        items: (getGaVariants(data) || []).slice(0, MAX_LIST_TRACK),
        event_id: eid
    } as IGaEvent);

    gtmEvent('ViewList', {
        currency,
        items: (getGaVariants(data) || []).slice(0, MAX_LIST_TRACK),
        event_id: eid
    } as IGaEvent);

    //fbEvent('ViewContent', getFbEventData(data, false), eid)
}

// 商品列表中点击商品链接
export function trackProductClick(data: ITrackVariant) {
    const variant = getSpVariants([data])[0];
    spProductClick(variant);
    appProductClick(variant);
}

// 商品页展示
export function trackProductImpression(data: ITrackVariant) {
    const eid = generateTrackEventId();
    const currency = getCurrency();
    const list: Record<string, any>[] = getSpVariants([data])
    list.forEach(item => delete item.quantity);

    spProductsImpression('detail', list);

    gaEvent('view_item', {
        currency,
        items: getGaVariants([data]),
        event_id: eid
    } as IGaEvent);

    gtmEvent('ViewItem', {
        currency,
        items: getGaVariants([data]),
        event_id: eid
    } as IGaEvent);

    //fbEvent('ViewContent', getFbEventData([data], false), eid);

}

// 添加购物车
export function trackAddToCart(variants: ITrackProducts) {
    const currency = getCurrency();
    const gaAndFbVariants = getGaVariants(variants);
    const eid = generateTrackEventId();

    const spVariants = getSpVariants(variants);

    spCartItem('add', spVariants);

    appAddToCart(spVariants);

    gaEvent('add_to_cart', () => ({
        currency,
        items: gaAndFbVariants,
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    gtmEvent('AddToCart', () => ({
        currency,
        items: gaAndFbVariants,
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    //fbEvent('AddToCart', getFbEventData(variants, true), eid);
}

// 删除购物车商品
export function trackRemoveFromCart(variants: ITrackVariant[]) {
    const currency = getCurrency();
    const gaAndFbVariants = getGaVariants(variants);
    const eid = generateTrackEventId();

    spCartItem('remove', getSpVariants(variants));

    gaEvent('remove_from_cart', () => ({
        currency,
        items: gaAndFbVariants,
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    gtmEvent('RemoveFromCart', () => ({
        currency,
        items: gaAndFbVariants,
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    //fbCustomEvent('RemoveFromCart', getFbEventData(variants, true), eid);
}

// 浏览购物车
export function trackCartImpression(data: ITrackCheckoutData) {
    const orderInfo = getOrderInfo(data);
    //order id is cartId, which is one2one mapping with user
    const eid = generateTrackEventId();

    spCheckout('view', getSpVariants(data.items), orderInfo);

    gaEvent('view_cart', () => ({
        ...orderInfo,
        items: getGaVariants(data.items),
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    gtmEvent('ViewCart', () => ({
        ...orderInfo,
        items: getGaVariants(data.items),
        event_id: globalCartId + '.' + eid
    } as IGaEvent));
}

// 购物车点击下一步, 普通 checkout step 是 1, buy now 是 11， 未支付订单 checkout 是 111
export function trackInitialCheckout(data: ITrackCheckoutData, step?: 1 | 11 | 111) {
    const gaVariants = getGaVariants(data.items);
    const orderInfo = getOrderInfo(data, step || 1);
    const eid = generateTrackEventId();

    spCheckout('checkout', getSpVariants(data.items), orderInfo);

    gaEvent('begin_checkout', () => ({
        ...orderInfo,
        items: gaVariants,
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    gtmEvent('InitialCheckout', () => ({
        ...orderInfo,
        items: gaVariants,
        event_id: globalCartId + '.' + eid
    } as IGaEvent));

    /*
    fbEvent('InitiateCheckout', getFbEventData(data.items, true, {
        value: orderInfo.revenue,
        num_items: data.items.length
    }), eid);
    */
}

// Buy Now
export function trackBuyNow(data: ITrackCheckoutData) {
    trackInitialCheckout(data, 11);
}

// 未支付订单checkout
export function trackUnpaidOrderCheckout(data: ITrackCheckoutData) {
    trackInitialCheckout(data, 111);
}

// checkout click
export function trackCheckoutOption(data: ITrackCheckoutData) {
    const gaVariants = getGaVariants(data.items);
    const orderInfo = getOrderInfo(data, 2);
    //order id is valid from here
    const eid = orderInfo.id + '.' + generateTrackEventId();

    spCheckout('checkout_option', getSpVariants(data.items), orderInfo);

    gaEvent('add_payment_info', {
        ...orderInfo,
        items: gaVariants,
        event_id: eid
    } as IGaEvent);

    gtmEvent('CheckoutOption', {
        ...orderInfo,
        items: gaVariants,
        event_id: eid
    } as IGaEvent);

    /*
    fbEvent('AddPaymentInfo', getFbEventData(data.items, true, {
        value: orderInfo.revenue,
        num_items: data.items.length
    }), eid);
    */
}

// 支付成功页面
export function trackPurchase(data: ITrackCheckoutData, source = '', userId: string) {
    const gaVariants = getGaVariants(data.items);
    const orderInfo = getOrderInfo(data, 3);
    const {coupon, ...rest } = orderInfo;
    const eid = orderInfo.id as string;

    // coupon 只传给 GA
    spCheckout('purchase', getSpVariants(data.items), rest);

    gaEvent('purchase', {
        ...orderInfo,
        items: gaVariants,
        event_id: eid
    } as IGaEvent);

    gtmEvent('Purchase', {
        ...orderInfo,
        items: gaVariants,
        event_id: eid
    } as IGaEvent);

    /*
    fbEvent('Purchase', getFbEventData(data.items, true, {
        value: orderInfo.revenue,
        num_items: data.items.length
    }), eid);
    */
}

// Subscribe
export function trackLead(data: ITrackLead) {
    const eid = generateTrackEventId();

    spStructEvent({
        category: 'lead',
        action: 'submit',
        label: data.name,
        property: data.info,
        value: data.revenue,
    });

    gaEvent('generate_lead', {
        ...data,
        event_id: eid
    });

    gtmEvent('Lead', {
        ...data,
        event_id: eid
    });

    /*
    fbEvent('Lead', {
      currency: data.currency,
      value: data.revenue,
    }, eid);
    */
}

// Login
export function trackLogin() {
    const eid = generateTrackEventId();

    spStructEvent({
        category: 'login',
        action: 'submit',
    });

    gaEvent('login', {
        event_id: eid
    });

    gtmEvent('Login', {
        event_id: eid
    });
}

// Register
export function trackRegister() {
    const eid = generateTrackEventId();

    spStructEvent({
        category: 'register',
        action: 'submit',
    });

    gaEvent('sign_up', {
        event_id: eid
    });

    gtmEvent('Register', {
        event_id: eid
    });
}

export function isProduct(data: Record<string, any>): boolean {
    return 'shopPrice' in data;
}

export function isElement(data: Record<string, any>): boolean {
    return 'target' in data;
}

export function getImpressedKey(data: ITrackerData): string {
    if (isProduct(data)) {
        if ('track' in data && data.track) {
            return `${data.track.list}|${data.position}|${data.track.variant}`;
        } else {
            const { listTrackCode, position, variantTrackCode } = data as ITrackVariant;
            return `${listTrackCode}|${position}|${variantTrackCode}`;
        }
    } else {
        const { module, position, target } = data as ITrackElementData;
        return `${module}|${position}|${target}`;
    }
}

function notImpressed(data: ITrackElementData): boolean {
    const { action } = data;
    if (action === 'click') {
        return true;
    }
    const key = getImpressedKey(data);
    if (impressed[key] === 1) {
        return false;
    }
    impressed[key] = 1;
    return true;
}

// 普通元素 CTR 打点，impression/click
export function trackElement(data: ITrackElementData | ITrackElementData[]) {
    let elements: ISpElementData[] = [];
    if (Array.isArray(data)) {
        elements = data.filter(item => notImpressed(item));
    } else {
        if (notImpressed(data)) {
            elements.push(data);
        }
    }
    if (elements.length === 0) {
        return;
    }
    const rootState = getStoreState('rootStore');
    const action = elements[0].action;

    const ctrData = {
        event: {
            schema: 'iglu:com.vivosun/action/jsonschema/1-0-3',
            data: {
                action,
                viewType: rootState.viewType,
                canonicalUrl: getClientCanonicalUrl()
            }
        },
        context: elements.map(item => ({
            schema: 'iglu:com.vivosun/action-context/jsonschema/1-0-0',
            data: {
                ...filterFields(item, [
                    'action',
                    'targetType',
                    'target',
                    'relTarget',
                    'module',
                    'position',
                    'creative',
                    'content',
                    'value',
                ]),
                position: String(item.position || '') //position 强制转换为字符串格式
            }
        }))
    };
    spElement(ctrData);

    const nativeCtrData = [ctrData.event, ...ctrData.context];
    if (action === 'impr') {
        appCtrImpressions(nativeCtrData);
    } else {
        appCtrClick(nativeCtrData);
    }
}

export function displayCtr(moduleId: string) {
    // console.log('displayCtr', moduleId);
    if (!!moduleId) {
        document.dispatchEvent(new CustomEvent('displayCtr', {
            detail: { moduleId }
        }));
    }
}

export function loadCtr() {
    document.dispatchEvent(new CustomEvent('loadCtr'));
}

export function loadMonkeyProducts() {
    document.dispatchEvent(new CustomEvent('loadMonkeyProducts'));
}